import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Calendar as CalendarIcon, TrendingUp, ArrowLeft, RefreshCw,
  Clock, ArrowUpRight, ArrowDownRight, ChevronRight
} from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import api from '../lib/api';

// API functions
const getAnalyticsSummary = (params) => api.get('/analytics/summary', { params });
const getRevenueTrend = (params) => api.get('/analytics/revenue-trend', { params });
const getPaymentHealth = (params) => api.get('/analytics/payment-health', { params });
const getRevenueMix = (params) => api.get('/analytics/revenue-mix', { params });
const getUtilizationHeatmap = (params) => api.get('/analytics/utilization-heatmap', { params });

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today', grouping: 'daily' },
  { value: 'week', label: 'This Week', grouping: 'daily' },
  { value: 'month', label: 'This Month', grouping: 'weekly' },
  { value: 'custom', label: 'Custom', grouping: 'daily' },
];

// Helper to format date as YYYY-MM-DD
const formatDateForApi = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to format date for display
const formatDateDisplay = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Control state
  const [rangeType, setRangeType] = useState('week');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMode, setCalendarMode] = useState('single'); // 'single' or 'range'
  const [revenueMode, setRevenueMode] = useState('cash');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trainingMixExpanded, setTrainingMixExpanded] = useState(false);
  
  // Auto-select grouping based on date range
  const grouping = useMemo(() => {
    if (rangeType === 'custom' && customDateRange.from && customDateRange.to) {
      const days = Math.ceil((customDateRange.to - customDateRange.from) / (1000 * 60 * 60 * 24)) + 1;
      if (days <= 31) return 'daily';
      return 'weekly';
    }
    const option = RANGE_OPTIONS.find(opt => opt.value === rangeType);
    return option?.grouping || 'daily';
  }, [rangeType, customDateRange]);
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [paymentHealth, setPaymentHealth] = useState(null);
  const [revenueMix, setRevenueMix] = useState(null);
  
  const fetchAllData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = { range_type: rangeType };
      
      if (rangeType === 'custom' && customDateRange.from && customDateRange.to) {
        params.custom_start = formatDateForApi(customDateRange.from);
        params.custom_end = formatDateForApi(customDateRange.to);
      }
      
      const [summaryRes, trendRes, healthRes, mixRes] = await Promise.all([
        getAnalyticsSummary(params),
        getRevenueTrend({ ...params, grouping }),
        getPaymentHealth(params),
        getRevenueMix(params),
      ]);
      
      setSummary(summaryRes.data);
      setRevenueTrend(trendRes.data);
      setPaymentHealth(healthRes.data);
      setRevenueMix(mixRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [rangeType, grouping, customDateRange]);
  
  useEffect(() => {
    if (rangeType === 'custom' && (!customDateRange.from || !customDateRange.to)) {
      return;
    }
    fetchAllData();
  }, [fetchAllData]);
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  const formatCurrencyShort = (value) => {
    if (value >= 10000) {
      return `₱${(value / 1000).toFixed(0)}k`;
    }
    return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Get displayed revenue based on mode
  // Total collected from customers (gross)
  const totalCollected = summary ? (revenueMode === 'cash' ? summary.kpis.total_revenue : summary.kpis.earned_revenue) : 0;
  // Net Revenue = amount retained by Turning Point (excludes supplier obligations)
  const netRevenue = summary
    ? (revenueMode === 'cash' ? summary.kpis.net_revenue_cash : summary.kpis.net_revenue_earned) ?? totalCollected
    : 0;
  const supplierPayables = summary
    ? (revenueMode === 'cash' ? summary.kpis.supplier_payables_cash : summary.kpis.supplier_payables_earned) ?? 0
    : 0;
  const receivables = summary ? summary.kpis.revenue_at_risk : 0;
  const revenuePerCourtHour = summary && summary.kpis.booked_hours > 0 
    ? summary.kpis.earned_revenue / summary.kpis.booked_hours 
    : 0;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/operator')}
              className="p-1.5 rounded hover:bg-gray-50 transition-colors"
              data-testid="back-to-schedule-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h1 className="text-base font-medium text-gray-900">Financial Overview</h1>
              <p className="text-xs text-gray-400">
                {summary?.period?.start_date} — {summary?.period?.end_date}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchAllData(true)}
              disabled={refreshing}
              className="p-1.5 rounded hover:bg-gray-50 transition-colors"
              data-testid="refresh-analytics-btn"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <span className="text-xs text-gray-400">{user?.name}</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Control Bar - Minimal */}
        <div className="flex flex-wrap items-center gap-4" data-testid="control-bar">
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button 
                  className="p-1.5 rounded hover:bg-white transition-colors"
                  data-testid="calendar-picker-btn"
                >
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                  <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
                    <button
                      onClick={() => setCalendarMode('single')}
                      className={`px-3 py-1.5 font-medium transition-colors ${calendarMode === 'single' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                      data-testid="cal-mode-single"
                    >
                      Single Date
                    </button>
                    <button
                      onClick={() => setCalendarMode('range')}
                      className={`px-3 py-1.5 font-medium transition-colors ${calendarMode === 'range' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                      data-testid="cal-mode-range"
                    >
                      Date Range
                    </button>
                  </div>
                </div>
                {calendarMode === 'single' ? (
                  <Calendar
                    mode="single"
                    selected={customDateRange.from}
                    onSelect={(date) => {
                      if (date) {
                        setCustomDateRange({ from: date, to: date });
                        setRangeType('custom');
                        setCalendarOpen(false);
                      }
                    }}
                    numberOfMonths={1}
                  />
                ) : (
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from && range?.to) {
                        setRangeType('custom');
                        setCalendarOpen(false);
                      }
                    }}
                    numberOfMonths={2}
                  />
                )}
                {customDateRange.from && customDateRange.to && (
                  <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {formatDateDisplay(customDateRange.from)} — {formatDateDisplay(customDateRange.to)}
                    </span>
                    <button
                      onClick={() => {
                        setCustomDateRange({ from: null, to: null });
                        setRangeType('week');
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            
            <div className="flex rounded-md border border-gray-200 overflow-hidden bg-white">
              {RANGE_OPTIONS.filter(opt => opt.value !== 'custom').map(opt => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setRangeType(opt.value);
                    setCustomDateRange({ from: null, to: null });
                  }}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    rangeType === opt.value
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  data-testid={`range-${opt.value}-btn`}
                >
                  {opt.label}
                </button>
              ))}
              {rangeType === 'custom' && customDateRange.from && customDateRange.to && (
                <span className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white">
                  {formatDateDisplay(customDateRange.from)} — {formatDateDisplay(customDateRange.to)}
                </span>
              )}
            </div>
          </div>
          
          {/* Revenue Mode Toggle */}
          <div className="flex rounded-md border border-gray-200 overflow-hidden bg-white ml-auto">
            <button
              onClick={() => setRevenueMode('cash')}
              className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                revenueMode === 'cash'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="mode-cash-btn"
            >
              Cash
            </button>
            <button
              onClick={() => setRevenueMode('earned')}
              className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                revenueMode === 'earned'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="mode-earned-btn"
            >
              TPP Revenue
            </button>
          </div>
        </div>
        
        {/* Primary KPI Row - The 4 numbers that matter */}
        <div className="grid grid-cols-12 gap-4" data-testid="kpi-snapshot">
          {/* Cash Collected — gross from customers */}
          <div className="col-span-6 md:col-span-3 bg-white rounded-lg border border-gray-100 p-5" data-testid="kpi-cash-collected">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              Cash Collected
            </p>
            <p className="text-3xl font-semibold text-gray-900 tracking-tight">
              {formatCurrency(totalCollected)}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">
              Gross from customers · {revenueMode === 'cash' ? 'Paid' : 'TPP Revenue basis'}
            </p>
          </div>

          {/* TPP Revenue — retained by Turning Point Pickleball */}
          <div
            className="col-span-6 md:col-span-3 rounded-lg border p-5"
            data-testid="kpi-tp-revenue"
            style={{ backgroundColor: '#F7FBEA', borderColor: '#D9E6B5' }}
          >
            <p className="text-xs font-medium uppercase tracking-wide mb-1 text-gray-400">
              TPP Revenue
            </p>
            <p className="text-3xl font-semibold tracking-tight text-gray-900">
              {formatCurrency(netRevenue)}
            </p>
            <p className="text-[11px] mt-1.5 text-gray-500">
              Retained by Turning Point
            </p>
          </div>

          {/* Supplier Payables */}
          <div className="col-span-6 md:col-span-3 bg-white rounded-lg border border-gray-100 p-5" data-testid="kpi-supplier-payables">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              Supplier Payables
            </p>
            <p className={`text-3xl font-semibold tracking-tight ${supplierPayables > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
              {formatCurrency(supplierPayables)}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">
              Owed to equipment supplier
            </p>
          </div>

          {/* Utilization */}
          <div className="col-span-6 md:col-span-3 bg-white rounded-lg border border-gray-100 p-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              Utilization
            </p>
            <p className="text-3xl font-semibold text-gray-900 tracking-tight" data-testid="kpi-utilization">
              {summary?.kpis?.utilization_rate || 0}%
            </p>
            <p className="text-[11px] text-gray-500 mt-1.5">
              Prime <span className="font-medium text-gray-700">{summary?.kpis?.prime_utilization || 0}%</span> · {summary?.kpis?.booked_hours || 0}/{summary?.kpis?.total_court_hours || 0}h
            </p>
            <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-400 rounded-full transition-all"
                style={{ width: `${summary?.kpis?.utilization_rate || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Secondary metrics row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-6 md:col-span-6 bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              Receivables
            </p>
            <p className={`text-xl font-semibold tracking-tight ${receivables > 0 ? 'text-gray-900' : 'text-gray-300'}`} data-testid="kpi-receivables">
              {formatCurrency(receivables)}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">Pending payment or approval</p>
          </div>
          <div className="col-span-6 md:col-span-6 bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              ₱ / Court-Hour
            </p>
            <p className="text-xl font-semibold text-gray-900 tracking-tight" data-testid="kpi-revenue-per-hour">
              {formatCurrency(Math.round(revenuePerCourtHour))}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">Based on earned revenue</p>
          </div>
        </div>
        
        {/* Revenue Trend */}
        <div className="bg-white rounded-lg border border-gray-100 p-6" data-testid="revenue-trend-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-sm font-medium text-gray-900">Revenue Trend</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {revenueTrend?.range?.start} to {revenueTrend?.range?.end}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${revenueMode === 'cash' ? 'bg-brand-teal' : 'bg-brand-teal-tint'}`} />
                <span className={revenueMode === 'cash' ? 'text-gray-700' : 'text-gray-400'}>Cash</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: revenueMode === 'earned' ? '#6F8C00' : 'rgba(111,140,0,0.35)' }}
                />
                <span className={revenueMode === 'earned' ? 'text-gray-700' : 'text-gray-400'}>TPP Revenue</span>
              </div>
            </div>
          </div>
          
          <div className="h-56">
            {revenueTrend?.data?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueTrend.data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis 
                    dataKey="period" 
                    tick={{ fontSize: 10, fill: '#9CA3AF' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(val) => {
                      const [,m,d] = val.split('-');
                      const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      return `${months[parseInt(m)]} ${parseInt(d)}`;
                    }}
                  />
                  <YAxis 
                    tick={{ fontSize: 10, fill: '#9CA3AF' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(val) => formatCurrencyShort(val)}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const [, m, d] = (label || '').split('-');
                      const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const dateLabel = `${months[parseInt(m)]} ${parseInt(d)}`;
                      return (
                        <div style={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                          <div style={{ color: '#374151', marginBottom: 4, fontWeight: 500 }}>{dateLabel}</div>
                          {payload.map((p) => {
                            const isCash = p.dataKey === 'cash_revenue';
                            const c = isCash ? '#0F8F8D' : '#6F8C00';
                            const name = isCash ? 'Cash' : 'TPP Revenue';
                            return (
                              <div key={p.dataKey} style={{ color: c, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                <span>{name}</span>
                                <span style={{ fontWeight: 600 }}>{formatCurrency(p.value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                  <Bar 
                    dataKey="cash_revenue" 
                    fill={revenueMode === 'cash' ? '#0F8F8D' : 'rgba(15,143,141,0.25)'}
                    radius={[3, 3, 0, 0]}
                  />
                  <Bar 
                    dataKey="earned_revenue" 
                    fill={revenueMode === 'earned' ? '#6F8C00' : 'rgba(111,140,0,0.25)'}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-400">
                No data for selected period
              </div>
            )}
          </div>
          
          {/* Comparison text */}
          {summary?.comparison && (
            <div className="mt-4 pt-3 border-t border-gray-50">
              {(() => {
                const change = revenueMode === 'cash' 
                  ? summary.comparison.cash_change_pct 
                  : summary.comparison.earned_change_pct;
                const isPositive = change > 0;
                const isNeutral = change === 0;
                const isSignificantDecline = change < -10;
                
                return (
                  <p className={`text-xs flex items-center gap-1 ${
                    isNeutral ? 'text-gray-400' : 
                    isPositive ? 'text-brand-teal' : 
                    isSignificantDecline ? 'text-status-held' : 'text-gray-500'
                  }`}>
                    {isPositive ? '▲' : isNeutral ? '―' : '▼'}
                    <span className="font-medium">
                      {isPositive ? '+' : ''}{change}%
                    </span>
                    <span className="text-gray-400 ml-1">vs previous period</span>
                  </p>
                );
              })()}
            </div>
          )}
        </div>
        
        {/* Bottom Row: Revenue Mix + Accounts Receivable */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Mix - Horizontal Bars */}
          <div className="bg-white rounded-lg border border-gray-100 p-6" data-testid="revenue-mix-chart">
            <h2 className="text-sm font-medium text-gray-900 mb-4">Revenue Mix</h2>
            
            {revenueMix?.breakdown?.length > 0 ? (
              <div className="space-y-4">
                {revenueMix.breakdown.map((item, index) => {
                  const barColors = ['#313638', '#6F8C00', '#0E6F6C', '#6E6AC9', '#3FAF74', '#D97706'];
                  const percentage = revenueMix.total > 0 ? (item.revenue / revenueMix.total) * 100 : 0;
                  const isTraining = item.type === 'Training Packages';
                  const bd = item.breakdown;
                  
                  return (
                    <div key={item.type}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-600">{item.type}</span>
                          {isTraining && item.revenue > 0 && (
                            <button
                              data-testid="training-mix-toggle"
                              onClick={() => setTrainingMixExpanded(!trainingMixExpanded)}
                              aria-label={trainingMixExpanded ? 'Hide breakdown' : 'Show breakdown'}
                              className="text-gray-400 hover:text-gray-700 p-0.5 -ml-0.5 transition-transform"
                              style={{ transform: trainingMixExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                            >
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-medium text-gray-900">{formatCurrency(item.revenue)}</span>
                          <span className="text-xs text-gray-400 w-10 text-right">{item.percentage}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: barColors[index]
                          }}
                        />
                      </div>
                      {isTraining && trainingMixExpanded && bd && (
                        <div
                          data-testid="training-mix-breakdown"
                          className="mt-2 rounded-lg px-3 py-2 space-y-1 border"
                          style={{ backgroundColor: '#F7FBEA', borderColor: '#D9E6B5' }}
                        >
                          <div className="flex justify-between text-[11px]">
                            <span className="text-gray-600">Customer Payments</span>
                            <span className="font-medium text-gray-900">{formatCurrency(bd.collected)}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span style={{ color: '#6F8C00' }}>Turning Point Share</span>
                            <span className="font-medium" style={{ color: '#6F8C00' }}>{formatCurrency(bd.tp_share)}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span className="text-gray-500">Supplier Share</span>
                            <span className="font-medium text-gray-700">{formatCurrency(bd.supplier_share)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-sm text-gray-400">
                No revenue data
              </div>
            )}
          </div>
          
          {/* Accounts Receivable */}
          <div className="bg-white rounded-lg border border-gray-100 p-6" data-testid="accounts-receivable-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-900">Accounts Receivable</h2>
              <button
                onClick={() => navigate('/operator')}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                View Tasks
              </button>
            </div>
            
            {/* Total Receivables Header */}
            <div className="mb-5 pb-4 border-b border-gray-100">
              <p className="text-xs text-gray-400 mb-1">Total Receivables</p>
              <div className="flex items-baseline gap-2">
                <p className={`text-2xl font-semibold ${receivables > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                  {formatCurrency(receivables)}
                </p>
                {receivables > 0 && netRevenue > 0 && (
                  <span className="text-xs text-amber-600">
                    ({Math.round((receivables / (revenueMode === 'cash' ? summary?.kpis?.earned_revenue : summary?.kpis?.earned_revenue)) * 100)}% of revenue)
                  </span>
                )}
              </div>
            </div>
            
            {paymentHealth && (
              <div className="space-y-3">
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-500">Pending Payments</span>
                  <span className={`text-xs font-medium ${paymentHealth.pending_payment > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                    {paymentHealth.pending_payment}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-500">Pending Approval</span>
                  <span className={`text-xs font-medium ${paymentHealth.pending_approval > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                    {paymentHealth.pending_approval}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-500">Overdue</span>
                  <span className={`text-xs font-medium ${paymentHealth.overdue > 0 ? 'text-status-held' : 'text-gray-300'}`}>
                    {paymentHealth.overdue}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-500">Cross-Date Receivables</span>
                  <span className={`text-xs font-medium ${paymentHealth.other_days_pending > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                    {paymentHealth.other_days_pending}
                  </span>
                </div>
                
                {/* On-time rate */}
                <div className="pt-4 mt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">On-Time Collection</span>
                    <span className={`text-xs font-medium ${paymentHealth.on_time_rate >= 80 ? 'text-emerald-600' : 'text-gray-600'}`}>
                      {paymentHealth.on_time_rate}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Court Utilization Heatmap */}
        <CourtUtilization />

        {/* Period Footer */}
        <div className="text-center pt-4">
          <p className="text-[10px] text-gray-400">
            {summary?.period?.num_days} day{summary?.period?.num_days !== 1 ? 's' : ''} · Updated just now
          </p>
        </div>
      </main>
    </div>
  );
}

// ============ Court Utilization Heatmap ============
const HEATMAP_RANGES = [
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
];

// Utilization color (TPP teal scale). Closed/gray handled separately.
function utilColor(rate) {
  if (rate <= 0) return { bg: '#F1F3F4', fg: '#9AA0A6' };      // light gray (available, unused)
  if (rate <= 25) return { bg: '#CCE8EA', fg: '#313638' };     // very light teal
  if (rate <= 50) return { bg: '#8FD0D4', fg: '#313638' };     // light teal
  if (rate <= 75) return { bg: '#3FA7AD', fg: '#FFFFFF' };     // medium teal
  return { bg: '#008C95', fg: '#FFFFFF' };                      // dark teal
}

const CLOSED_STYLE = {
  backgroundColor: '#E2E5E8',
  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(100,116,139,0.28) 4px, rgba(100,116,139,0.28) 5px)',
  color: '#64748B',
};

function CourtUtilization() {
  const [range, setRange] = useState('last_30');
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [calOpen, setCalOpen] = useState(false);
  const [courtId, setCourtId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null); // {day, hour_label, cell}

  const fmtApi = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const fetchHeatmap = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range_type: range, court_id: courtId };
      if (range === 'custom' && customRange.from && customRange.to) {
        params.custom_start = fmtApi(customRange.from);
        params.custom_end = fmtApi(customRange.to);
      }
      const res = await getUtilizationHeatmap(params);
      setData(res.data);
      setSelectedCell(null);
    } catch (err) {
      console.error('Failed to fetch utilization heatmap:', err);
    } finally {
      setLoading(false);
    }
  }, [range, courtId, customRange]);

  useEffect(() => {
    if (range === 'custom' && (!customRange.from || !customRange.to)) return;
    fetchHeatmap();
  }, [fetchHeatmap]);

  const summary = data?.summary;

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-6 mt-6" data-testid="court-utilization-section">
      {/* Header + filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-sm font-medium text-gray-900">Court Utilization</h2>
          <p className="text-xs text-gray-400 mt-0.5">Booked vs. available court-hours · Mon–Sat</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Court filter */}
          <select
            data-testid="heatmap-court-filter"
            value={courtId}
            onChange={(e) => setCourtId(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#008C95]"
          >
            <option value="all">All Courts</option>
            {(data?.courts || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {/* Range filter */}
          <div className="flex items-center gap-1 bg-gray-50 rounded-md p-0.5">
            {HEATMAP_RANGES.map((r) => (
              <button
                key={r.value}
                data-testid={`heatmap-range-${r.value}`}
                onClick={() => setRange(r.value)}
                className={`text-xs px-2.5 py-1 rounded transition-colors ${range === r.value ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="heatmap-custom-range-btn"
                  className="text-xs border border-gray-200 rounded-md px-2.5 py-1.5 text-gray-700 flex items-center gap-1.5 hover:bg-gray-50"
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {customRange.from && customRange.to
                    ? `${formatDateDisplay(customRange.from)} – ${formatDateDisplay(customRange.to)}`
                    : 'Pick dates'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={customRange}
                  onSelect={(r) => { setCustomRange(r || { from: null, to: null }); if (r?.from && r?.to) setCalOpen(false); }}
                  numberOfMonths={1}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Summary insights */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-lg border border-gray-100 p-3" data-testid="heatmap-overall">
            <p className="text-[11px] text-gray-400 mb-1">Overall Utilization</p>
            <p className="text-xl font-semibold" style={{ color: '#008C95' }}>{summary.overall_utilization}%</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{summary.total_booked_court_hours} / {summary.total_available_court_hours} court-hrs</p>
          </div>
          <div className="rounded-lg border border-gray-100 p-3" data-testid="heatmap-busiest">
            <p className="text-[11px] text-gray-400 mb-1">Busiest Time</p>
            {summary.busiest ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{summary.busiest.day} · {summary.busiest.hour_label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{summary.busiest.rate}% utilized</p>
              </>
            ) : <p className="text-sm text-gray-300">—</p>}
          </div>
          <div className="rounded-lg border border-gray-100 p-3" data-testid="heatmap-underused">
            <p className="text-[11px] text-gray-400 mb-1">Most Underused</p>
            {summary.underused ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{summary.underused.day} · {summary.underused.hour_label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{summary.underused.rate}% utilized</p>
              </>
            ) : <p className="text-sm text-gray-300">—</p>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-400">Loading heatmap…</div>
      ) : data && data.data?.length > 0 ? (
        <div className="overflow-x-auto">
          <div className="min-w-[520px]">
            {/* Column headers */}
            <div className="grid" style={{ gridTemplateColumns: `64px repeat(${data.days.length}, 1fr)` }}>
              <div />
              {data.days.map((d) => (
                <div key={d} className="text-center text-xs font-medium text-gray-500 pb-2">{d}</div>
              ))}
            </div>
            {/* Rows */}
            {data.data.map((row) => (
              <div
                key={row.hour}
                className="grid gap-1 mb-1"
                style={{ gridTemplateColumns: `64px repeat(${data.days.length}, 1fr)` }}
              >
                <div className="text-[11px] text-gray-400 flex items-center justify-end pr-2">{row.hour_label}</div>
                {data.days.map((d) => {
                  const cell = row[d];
                  const isClosed = cell.status === 'closed';
                  const style = isClosed ? CLOSED_STYLE : utilColor(cell.rate);
                  const isSel = selectedCell && selectedCell.day === d && selectedCell.hour === row.hour;
                  return (
                    <button
                      key={d}
                      data-testid={`heatmap-cell-${d}-${row.hour}`}
                      onClick={() => !isClosed && setSelectedCell({ day: d, hour: row.hour, hour_label: row.hour_label, cell })}
                      disabled={isClosed}
                      title={isClosed ? 'Closed' : `${d} ${row.hour_label} · ${cell.rate}%`}
                      className={`h-8 rounded flex items-center justify-center text-[10px] font-medium transition-all ${isClosed ? 'cursor-not-allowed' : 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-[#313638]/30'} ${isSel ? 'ring-2 ring-offset-1 ring-[#313638]' : ''}`}
                      style={typeof style === 'object' && style.backgroundColor ? style : { backgroundColor: style.bg, color: style.fg }}
                    >
                      {isClosed ? 'N/A' : `${cell.rate}%`}
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 mt-4 text-[11px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#F1F3F4' }} /> 0%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#CCE8EA' }} /> 1–25%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#8FD0D4' }} /> 26–50%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#3FA7AD' }} /> 51–75%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#008C95' }} /> 76–100%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={CLOSED_STYLE} /> Closed</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-sm text-gray-400">No utilization data</div>
      )}

      {/* Cell detail panel */}
      {selectedCell && (
        <div
          className="mt-5 rounded-lg border p-4"
          style={{ backgroundColor: '#F4FAFA', borderColor: '#B8E0E2' }}
          data-testid="heatmap-cell-detail"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: '#313638' }}>
              {selectedCell.day} · {selectedCell.hour_label}–{formatHourLabel(selectedCell.hour + 1)}
            </h3>
            <button
              data-testid="heatmap-detail-close"
              onClick={() => setSelectedCell(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <p className="text-[11px] text-gray-400">Utilization</p>
              <p className="text-lg font-semibold" style={{ color: '#008C95' }}>{selectedCell.cell.rate}%</p>
            </div>
            <div>
              <p className="text-[11px] text-gray-400">Booked</p>
              <p className="text-lg font-semibold text-gray-900">{selectedCell.cell.booked_court_hours} <span className="text-xs font-normal text-gray-400">court-hrs</span></p>
            </div>
            <div>
              <p className="text-[11px] text-gray-400">Available</p>
              <p className="text-lg font-semibold text-gray-900">{selectedCell.cell.available_court_hours} <span className="text-xs font-normal text-gray-400">court-hrs</span></p>
            </div>
            <div>
              <p className="text-[11px] text-gray-400">Confirmed Bookings</p>
              <p className="text-lg font-semibold text-gray-900">{selectedCell.cell.booking_count}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatHourLabel(hour) {
  const h = hour >= 24 ? hour - 24 : hour;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

